import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, PenTool, GitMerge } from 'lucide-react';
const pillars = [{
  num: '01',
  icon: <Cpu size={28} className="text-blue-500" data-id="element-185" />,
  title: 'High-Performance Architecture',
  description: 'Faster, safer, conversion-optimized. We build on modern stacks where every millisecond is engineered for revenue.',
  image: 'https://cdn.magicpatterns.com/uploads/o9caM5MGnHLi5dq6Tay8RL/pcs_results.png',
  imageAlt: 'Performance comparison showing generic site score 30 vs PCS optimized site score 100'
}, {
  num: '02',
  icon: <PenTool size={28} className="text-blue-500" data-id="element-186" />,
  title: 'Premium Content Studio',
  description: 'Content that positions you as the authority, not a follower. Strategy-first storytelling that commands premium pricing.',
  image: 'https://cdn.magicpatterns.com/uploads/fKZYFYbQ38st9Ci63ctutD/pcs_content.png',
  imageAlt: 'PCS content calendar and social media strategy workspace'
}, {
  num: '03',
  icon: <GitMerge size={28} className="text-blue-500" data-id="element-187" />,
  title: 'The Synergy',
  description: 'The bridge between how you look and what you say. Architecture meets narrative to create an undeniable digital asset.',
  image: 'https://cdn.magicpatterns.com/uploads/ok8q2BnnWKKGu8FqVStE9h/pcs_performance.png',
  imageAlt: 'PCS energy brand visual representing the synergy of architecture and content'
}];
export function FrameworkSection() {
  return <section id="framework" className="py-24 md:py-32 bg-[#0A0A0A] relative" data-id="element-188">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" data-id="element-189" />

      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-190">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="text-center mb-20" data-id="element-191">
          <span className="text-blue-500 font-medium tracking-widest text-sm uppercase mb-4 block" data-id="element-192">
            The Solution
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-193">
            The PCS Framework
          </h2>
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto" data-id="element-194">
            We don't just design pages. We architect systems that elevate your
            brand's perceived value and drive measurable ROI.
          </p>
        </motion.div>

        <div className="space-y-20" data-id="element-195">
          {pillars.map((pillar, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          delay: 0.1
        }} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:direction-rtl' : ''}`} data-id="element-196">
              {/* Text Side */}
              <div className={`flex flex-col ${index % 2 === 1 ? 'lg:order-2' : ''}`} data-id="element-197">
                <div className="flex items-center gap-4 mb-6" data-id="element-198">
                  <div className="w-14 h-14 bg-[#111111] border border-white/10 rounded-full flex items-center justify-center shrink-0" data-id="element-199">
                    {pillar.icon}
                  </div>
                  <span className="text-5xl font-display font-bold text-white/5 select-none" data-id="element-200">
                    {pillar.num}
                  </span>
                </div>
                <h3 className="font-display text-3xl font-bold text-white mb-4" data-id="element-201">
                  {pillar.title}
                </h3>
                <p className="text-neutral-400 text-lg leading-relaxed max-w-lg" data-id="element-202">
                  {pillar.description}
                </p>
              </div>

              {/* Image Side */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-203">
                <div className="relative rounded-sm overflow-hidden border border-white/5 group" data-id="element-204">
                  <img src={pillar.image} alt={pillar.imageAlt} className="w-full h-auto object-cover aspect-[16/10] group-hover:scale-[1.02] transition-transform duration-500" data-id="element-205" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent pointer-events-none" data-id="element-206" />
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}